import { defineComponent, onMounted, ref } from 'vue'
import {
  createLegalDocument,
  getLegalDocumentDetail,
  getLegalDocumentList,
  offlineLegalDocument,
  publishLegalDocument,
  setCurrentLegalDocument,
  updateLegalDocument
} from './api'

const DOC_TYPE_OPTIONS = [
  { value: 'user_agreement', label: '用户协议' },
  { value: 'privacy_policy', label: '隐私政策' }
]

const STATUS_OPTIONS = [
  { value: 'draft', label: '草稿' },
  { value: 'published', label: '已发布' },
  { value: 'offline', label: '已下线' }
]

function extractPageData(response) {
  const payload = response?.data || {}
  const rows = Array.isArray(payload.data)
    ? payload.data
    : Array.isArray(payload.list)
      ? payload.list
      : Array.isArray(payload.results)
        ? payload.results
        : Array.isArray(payload)
          ? payload
          : []

  return {
    rows,
    page: Number(payload.page || payload.current_page || 1),
    totalPages: Number(payload.total_pages || payload.pages || 1),
    total: Number(payload.total || payload.count || rows.length)
  }
}

function pickDocumentId(item) {
  if (!item) return null
  const id = item.id != null ? item.id : item.document_id != null ? item.document_id : item.documentId
  const n = Number(id)
  return Number.isFinite(n) ? n : null
}

function pickCurrentFlag(item) {
  return Boolean(item?.is_current || item?.isCurrent)
}

export default defineComponent({
  name: 'LegalView',
  setup() {
    const documentList = ref([])
    const currentPage = ref(1)
    const totalPages = ref(1)
    const total = ref(0)
    const pageSize = ref(10)
    const selectedDocType = ref('')
    const selectedStatus = ref('')
    const searchQuery = ref('')
    const loading = ref(false)
    const saving = ref(false)
    const detailLoading = ref(false)
    const modalVisible = ref(false)
    const modalMode = ref('create')
    const currentDocument = ref(null)

    const form = ref({
      id: null,
      doc_type: 'user_agreement',
      title: '',
      version: '',
      content: '',
      status: 'draft',
      is_current: false,
      effective_time: ''
    })

    const formatDocType = (value) => {
      const matched = DOC_TYPE_OPTIONS.find((item) => item.value === value)
      return matched ? matched.label : value || '-'
    }

    const formatStatus = (value) => {
      const matched = STATUS_OPTIONS.find((item) => item.value === value)
      return matched ? matched.label : value || '-'
    }

    const statusClass = (value) => `status-tag status-${value || 'unknown'}`

    const resetForm = () => {
      form.value = {
        id: null,
        doc_type: selectedDocType.value || 'user_agreement',
        title: '',
        version: '',
        content: '',
        status: 'draft',
        is_current: false,
        effective_time: ''
      }
      currentDocument.value = null
    }

    const fillForm = (document) => {
      const source = document || {}
      form.value = {
        id: pickDocumentId(source),
        doc_type: source.doc_type || source.docType || 'user_agreement',
        title: source.title || '',
        version: source.version || '',
        content: source.content || '',
        status: source.status || 'draft',
        is_current: pickCurrentFlag(source),
        effective_time: source.effective_time || source.effectiveTime || ''
      }
      currentDocument.value = source
    }

    const fetchDocumentList = async () => {
      loading.value = true
      try {
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        }
        if (selectedDocType.value) params.doc_type = selectedDocType.value
        if (selectedStatus.value) params.status = selectedStatus.value
        if (searchQuery.value.trim()) params.search = searchQuery.value.trim()

        const response = await getLegalDocumentList(params)
        const data = extractPageData(response)
        documentList.value = data.rows
        currentPage.value = data.page
        totalPages.value = data.totalPages || 1
        total.value = data.total
      } catch (error) {
        console.error('获取协议列表失败:', error)
        alert(error.message || '获取协议列表失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const handleFilterChange = () => {
      currentPage.value = 1
      fetchDocumentList()
    }

    let searchTimer = null
    const handleSearch = () => {
      if (searchTimer) clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        currentPage.value = 1
        fetchDocumentList()
      }, 300)
    }

    const handlePageChange = (page) => {
      if (page < 1 || page > totalPages.value) return
      currentPage.value = page
      fetchDocumentList()
    }

    const openCreateModal = () => {
      resetForm()
      modalMode.value = 'create'
      modalVisible.value = true
    }

    const openDetailModal = async (document) => {
      const id = pickDocumentId(document)
      if (id == null) {
        alert('缺少协议 ID')
        return
      }

      modalMode.value = 'edit'
      modalVisible.value = true
      detailLoading.value = true
      fillForm(document)
      try {
        const response = await getLegalDocumentDetail(id)
        fillForm(response.data || document)
      } catch (error) {
        console.error('获取协议详情失败:', error)
        alert(error.message || '获取协议详情失败，请稍后重试')
      } finally {
        detailLoading.value = false
      }
    }

    const closeModal = () => {
      if (saving.value) return
      modalVisible.value = false
      resetForm()
    }

    const validateForm = () => {
      if (!form.value.doc_type) return '请选择协议类型'
      if (!form.value.title.trim()) return '请输入协议标题'
      if (!form.value.version.trim()) return '请输入版本号'
      if (!form.value.content.trim()) return '请输入协议内容'
      return ''
    }

    const handleSave = async () => {
      const message = validateForm()
      if (message) {
        alert(message)
        return
      }

      if (modalMode.value === 'edit' && form.value.status !== 'draft') {
        alert('只有草稿状态的协议可以编辑')
        return
      }

      saving.value = true
      try {
        if (modalMode.value === 'create') {
          await createLegalDocument({
            doc_type: form.value.doc_type,
            title: form.value.title.trim(),
            version: form.value.version.trim(),
            content: form.value.content,
            status: form.value.status,
            is_current: Boolean(form.value.is_current),
            effective_time: form.value.effective_time || null
          })
          alert('新增协议成功')
        } else {
          // POST /legal/admin/documents/update
          // 仅 draft 可改；字段：doc_type、title、version、content、effective_time
          await updateLegalDocument({
            id: form.value.id,
            doc_type: form.value.doc_type,
            title: form.value.title.trim(),
            version: form.value.version.trim(),
            content: form.value.content,
            effective_time: form.value.effective_time || null
          })
          alert('协议保存成功')
        }
        modalVisible.value = false
        resetForm()
        await fetchDocumentList()
      } catch (error) {
        console.error('保存协议失败:', error)
        alert(error.message || '保存协议失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const bumpVersion = (version) => {
      const raw = String(version || '').trim()
      const match = raw.match(/^(.*?)(\d+)(\D*)$/)
      if (!match) return raw ? `${raw}.1` : 'v1.0.1'
      const next = Number(match[2]) + 1
      return `${match[1]}${next}${match[3]}`
    }

    const openCreateFromDocument = async (document) => {
      const id = pickDocumentId(document)
      if (id == null) {
        alert('缺少协议 ID')
        return
      }

      loading.value = true
      try {
        const response = await getLegalDocumentDetail(id)
        const source = response.data || document
        resetForm()
        form.value = {
          id: null,
          doc_type: source.doc_type || source.docType || 'user_agreement',
          title: source.title || '',
          version: bumpVersion(source.version),
          content: source.content || '',
          status: 'draft',
          is_current: false,
          effective_time: source.effective_time || source.effectiveTime || ''
        }
        modalMode.value = 'create'
        modalVisible.value = true
      } catch (error) {
        console.error('复制协议失败:', error)
        alert(error.message || '复制协议失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const handlePublish = async (document) => {
      const id = pickDocumentId(document)
      if (id == null) return
      const isCurrent = confirm('发布后是否同时设为当前版本？')

      loading.value = true
      try {
        await publishLegalDocument(id, isCurrent)
        alert('发布成功')
        await fetchDocumentList()
      } catch (error) {
        console.error('发布协议失败:', error)
        alert(error.message || '发布协议失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const handleOffline = async (document) => {
      const id = pickDocumentId(document)
      if (id == null) return
      if (!confirm(`确定下线协议 "${document.title}" 吗？`)) return

      loading.value = true
      try {
        await offlineLegalDocument(id)
        alert('下线成功')
        await fetchDocumentList()
      } catch (error) {
        console.error('下线协议失败:', error)
        alert(error.message || '下线协议失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const handleSetCurrent = async (document) => {
      const id = pickDocumentId(document)
      if (id == null) return
      if (document.status !== 'published') {
        alert('只能把已发布协议设为当前版本')
        return
      }
      if (!confirm(`确定将 "${document.title}" 设为当前版本吗？`)) return

      loading.value = true
      try {
        await setCurrentLegalDocument(id)
        alert('已设为当前版本')
        await fetchDocumentList()
      } catch (error) {
        console.error('设置当前版本失败:', error)
        alert(error.message || '设置当前版本失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const isDraft = (document) => document?.status === 'draft'
    const isPublished = (document) => document?.status === 'published'
    const isCurrent = (document) => pickCurrentFlag(document)

    onMounted(() => {
      fetchDocumentList()
    })

    return {
      DOC_TYPE_OPTIONS,
      STATUS_OPTIONS,
      documentList,
      currentPage,
      totalPages,
      total,
      pageSize,
      selectedDocType,
      selectedStatus,
      searchQuery,
      loading,
      saving,
      detailLoading,
      modalVisible,
      modalMode,
      form,
      formatDocType,
      formatStatus,
      statusClass,
      pickDocumentId,
      isDraft,
      isPublished,
      isCurrent,
      fetchDocumentList,
      handleFilterChange,
      handleSearch,
      handlePageChange,
      openCreateModal,
      openDetailModal,
      closeModal,
      handleSave,
      openCreateFromDocument,
      handlePublish,
      handleOffline,
      handleSetCurrent
    }
  }
})
